import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@material-ui/core';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import AddIcon from '@material-ui/icons/Add';
import DOMPurify from 'dompurify';
import clsx from 'clsx';
import Authorize from 'components/Authorize';
import { permissionProfiles } from 'components/Authorize/permissionProfiles';
import NoteEditor from 'features/notes/NoteEditor';
import { NoteEntityTypes } from 'features/notes/notesConsts';
import { useGetEntityNotesQuery } from 'features/notes/notesSlice';
import { useState } from 'react';
import FormatService from 'services/formatService';
import { hotNoteCellStyles } from './HotNoteCellStyles';
import { ADD_HOT_NOTE_LABEL, HOT_NOTE_LABEL, HOT_NOTE_MAX_CHARS } from './HotNotePanelConsts';

const HotNoteCell = ({ activityId, hotNote }) => {
    const classes = hotNoteCellStyles();
    const [open, setOpen] = useState(false);
    // Subscribe only after the cell has been opened once - keeps the query alive so the
    // cell reflects saves immediately, without firing a request for every table row.
    const [hasOpened, setHasOpened] = useState(false);

    const { data: notes, isFetching, refetch } = useGetEntityNotesQuery(
        { entityId: activityId, entityType: NoteEntityTypes.ACTIVITY },
        { skip: !activityId || !hasOpened }
    );

    const hotNoteObj = notes?.find(n => n.isHotNote);

    // Once we've loaded the notes, trust them over the (possibly stale) table prop.
    const resolvedHotNote = hasOpened && notes !== undefined ? (hotNoteObj?.content ?? '') : hotNote;
    const plain = resolvedHotNote ? FormatService.cleanHtmlTags(resolvedHotNote).trim() : '';
    const display = plain ? (plain.length > 20 ? plain.slice(0, 20) + '…' : plain) : '—';
    const hasHotNote = !!plain;

    const baseNote = { entityId: activityId, entityType: NoteEntityTypes.ACTIVITY, isHotNote: true };

    const openDialog = () => {
        setHasOpened(true);
        setOpen(true);
    };
    const close = () => setOpen(false);

    // Empty-cell tooltip: only editors get the "Add" wording; everyone else sees the neutral label.
    const emptyTooltip = (
        <Authorize
            profile={permissionProfiles.NOTES.EDIT_HOT_NOTE}
            readonlyComponent={HOT_NOTE_LABEL}
            unauthorizedComponent={HOT_NOTE_LABEL}
        >
            {ADD_HOT_NOTE_LABEL}
        </Authorize>
    );

    // Non-editing dialog: shown while fetching and to read-only users. Editors get NoteEditor's own dialog.
    const viewDialog = (
        <Dialog open onClose={close} maxWidth="sm" fullWidth>
            <DialogTitle>{HOT_NOTE_LABEL}</DialogTitle>
            <DialogContent>
                {isFetching ? (
                    <CircularProgress size={20} />
                ) : hasHotNote ? (
                    <div
                        className={classes.readOnlyContent}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resolvedHotNote) }}
                    />
                ) : (
                    <span className={classes.empty}>No hot note</span>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={close}>Close</Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <>
            <Tooltip title={hasHotNote ? plain : emptyTooltip}>
                <div className={clsx(classes.cell, !hasHotNote && classes.empty)} onClick={openDialog}>
                    <span>{display}</span>
                    <Authorize profile={permissionProfiles.NOTES.EDIT_HOT_NOTE} readonlyComponent={<></>}>
                        {hasHotNote
                            ? <EditOutlinedIcon fontSize="small" className={classes.icon} />
                            : <AddIcon fontSize="small" className={classes.icon} />}
                    </Authorize>
                </div>
            </Tooltip>

            {open && (
                <Authorize
                    profile={permissionProfiles.NOTES.EDIT_HOT_NOTE}
                    readonlyComponent={viewDialog}
                    unauthorizedComponent={viewDialog}
                >
                    {isFetching ? viewDialog : (
                        <NoteEditor
                            note={hotNoteObj ?? baseNote}
                            onClose={close}
                            onSaved={refetch}
                            hideViewByClient
                            maxLength={HOT_NOTE_MAX_CHARS}
                            suppressSuccessToast
                        />
                    )}
                </Authorize>
            )}
        </>
    );
};

export default HotNoteCell;
